import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useSpring, useTransform, animate } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, className, delay = 0, y = 36, as = "div" }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: EASE, delay }}
    >
      {children}
    </MotionTag>
  );
}

export function Stagger({ children, className, delay = 0.08 }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={{ show: { transition: { staggerChildren: delay } } }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className, y = 30 }) {
  return (
    <motion.div
      className={className}
      variants={{ hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }}
    >
      {children}
    </motion.div>
  );
}

export function Overline({ children, light = false, className }) {
  return (
    <span
      data-testid="overline"
      className={cn(
        "inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.22em]",
        light ? "text-emerald-300" : "text-emerald2",
        className
      )}
    >
      <span className={cn("h-px w-6", light ? "bg-emerald-300/70" : "bg-emerald2/60")} />
      {children}
    </span>
  );
}

export function ArrowLink({ to, children, light = false, className, ...props }) {
  return (
    <Link
      to={to}
      className={cn(
        "group inline-flex items-center gap-2 font-semibold transition-all duration-300 hover:gap-3.5",
        light ? "text-emerald-300 hover:text-emerald-200" : "text-emeraldDeep hover:text-emerald2",
        className
      )}
      {...props}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform" />
    </Link>
  );
}

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5";
const btnVariants = {
  primary: "bg-ethanolOrange text-navy font-bold shadow-[0_8px_30px_rgba(194,240,60,0.35)] hover:bg-orangeDeep",
  outline: "border border-current text-current hover:bg-current/10",
  light: "bg-white text-navy hover:bg-lime/20",
  emerald: "bg-emerald2 text-white shadow-[0_8px_30px_rgba(51,161,74,0.3)] hover:bg-emeraldDeep",
};

export function CTAButton({ to, href, onClick, variant = "primary", children, className, type, ...props }) {
  const classes = cn(btnBase, btnVariants[variant], className);
  if (to) return <Link to={to} className={classes} onClick={onClick} {...props}>{children}</Link>;
  if (href) return <a href={href} className={classes} onClick={onClick} {...props}>{children}</a>;
  return <button type={type || "button"} onClick={onClick} className={classes} {...props}>{children}</button>;
}

export function AnimatedNumber({ value, decimals = 0, prefix = "", suffix = "", className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: "-40px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 80, damping: 22 });
  const display = useTransform(spring, (v) =>
    `${prefix}${v.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`
  );
  useEffect(() => {
    if (inView) mv.set(value);
  }, [inView, value, mv]);
  return <motion.span ref={ref} className={className}>{display}</motion.span>;
}

export function PageHero({ overline, title, subtitle, image, imageFallback, children }) {
  return (
    <section className="relative overflow-hidden bg-navy grain pt-36 pb-20 md:pt-44 md:pb-28">
      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          onError={(e) => {
            if (imageFallback && e.currentTarget.dataset.fb !== "1") {
              e.currentTarget.dataset.fb = "1";
              e.currentTarget.src = imageFallback;
            } else {
              e.currentTarget.style.display = "none";
            }
          }}
          className="absolute inset-0 h-full w-full object-cover opacity-[0.18]"
        />
      )}
      <div className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full bg-emerald2/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-80 w-80 rounded-full bg-sky2/20 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          {overline && <Overline light className="mb-5">{overline}</Overline>}
          <h1 className="max-w-4xl text-balance font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">{subtitle}</p>
          )}
          {children && <div className="mt-8">{children}</div>}
        </Reveal>
      </div>
    </section>
  );
}

export function SectionHeading({ overline, title, light = false, center = false, className }) {
  return (
    <div className={cn(center && "text-center flex flex-col items-center", className)}>
      {overline && <Overline light={light} className="mb-4">{overline}</Overline>}
      <h2 className={cn(
        "max-w-3xl text-balance font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl",
        light ? "text-white" : "text-navy"
      )}>
        {title}
      </h2>
    </div>
  );
}
